import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { finance } from '@/api/client';
import { fmtMoney } from '@/lib/money';
import { fmtDate } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';

/**
 * DREAM39 — "Visit Billing Detail" shortcut. Every Completed visit in the
 * period, billable or not, invoiced or not, so a coordinator can see
 * exactly why a card's buckets add up the way they do. Subject identity is
 * the reference number only — never the name (same rule as the invoice
 * PDF, AC7 DREAM40).
 */
export function VisitBillingDetailModal({
  open,
  onOpenChange,
  studyId,
  studyLabel,
  from,
  to,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  studyId: string;
  studyLabel: string;
  from: string;
  to: string;
}) {
  const { user } = useAuth();
  const detail = useQuery({
    queryKey: ['finance-visit-billing-detail', user?.activeSiteId, studyId, from, to],
    queryFn: () => finance.visitBillingDetail({ study_id: studyId, from, to }),
    enabled: open && !!user?.activeSiteId,
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Visit billing detail" description={studyLabel} width={920}>
      {detail.isLoading ? (
        <div className="flex items-center justify-center py-10 text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (detail.data?.rows.length ?? 0) === 0 ? (
        <EmptyState title="No completed visits in this period" />
      ) : (
        <div className="space-y-4">
          {detail.data!.visit_breakdown.length > 0 && (
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th>Visit</th>
                  <th className="text-right">Fixed cost</th>
                  <th className="text-right">Subjects</th>
                  <th className="text-right">Per subject</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {detail.data!.visit_breakdown.map((v) => (
                  <tr key={v.visit_name}>
                    <td>{v.visit_name}</td>
                    <td className="text-right text-ink-muted">
                      {v.fixed_fee_cents > 0 ? fmtMoney(v.fixed_fee_cents) : '—'}
                    </td>
                    <td className="text-right text-ink-muted">{v.subject_count}</td>
                    <td className="text-right text-ink-muted">{fmtMoney(v.per_subject_cents)}</td>
                    <td className="text-right font-medium">{fmtMoney(v.subtotal_cents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-medium">
                  <td colSpan={4}>Total for all visits</td>
                  <td className="text-right">
                    {fmtMoney(detail.data!.visit_breakdown.reduce((sum, v) => sum + v.subtotal_cents, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          <table className="data-table w-full text-sm">
            <thead>
              <tr>
                <th>Subject ref</th>
                <th>Visit #</th>
                <th>Visit</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Billable</th>
                <th>Invoiced</th>
              </tr>
            </thead>
            <tbody>
              {detail.data!.rows.map((r) => (
                <tr key={r.visit_id}>
                  <td>{r.subject_reference ?? '—'}</td>
                  <td>{r.visit_order ?? '—'}</td>
                  <td>{r.visit_name}</td>
                  <td>{r.actual_date ? fmtDate(r.actual_date) : '—'}</td>
                  <td>{fmtMoney(r.amount_cents)}</td>
                  <td>{r.billable ? 'Yes' : 'No'}</td>
                  <td>{r.invoiced ? r.invoice_number : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
